import { useEffect, useState } from "react";
import axios from "axios";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const RANGES = [["1d", "1D"], ["1w", "1W"], ["1m", "1M"], ["1y", "1Y"]];
const f2 = (v) => (v == null ? "—" : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const tick = (range) => (t) => {
  const d = new Date(t * 1000);
  if (range === "1d") return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (range === "1y") return d.toLocaleDateString([], { month: "short", year: "2-digit" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
};

const usePoll = (fn, ms, deps) => {
  useEffect(() => {
    fn();
    const id = setInterval(fn, ms);
    return () => clearInterval(id);
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

const Stat = ({ k, v, id }) => (
  <div className="pt-5 border-t border-[color:var(--line)]"><div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">{k}</div>
    <div className="gx-serif text-2xl md:text-3xl tabular-nums mt-1" data-testid={id}>{v}</div></div>
);

export const GtaxGoldChart = () => {
  const [range, setRange] = useState("1d");
  const [series, setSeries] = useState([]);
  const [spot, setSpot] = useState(null);
  usePoll(() => axios.get(`${API}/api/market/gold`).then((r) => setSpot(r.data)).catch(() => {}), 30000, []);
  usePoll(() => axios.get(`${API}/api/market/gold/history`, { params: { range } }).then((r) => setSeries(r.data.series)).catch(() => {}), range === "1d" ? 30000 : 300000, [range]);
  const first = series[0]?.v, last = series[series.length - 1]?.v;
  const chg = first && last ? last - first : null;
  const vals = series.map((p) => p.v);
  const up = (chg ?? 0) >= 0;
  return (
    <section className="py-24 gx-line" data-testid="gtax-gold-chart">
      <div className="gx-wrap">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <p className="gx-eyebrow">Market · XAU/USD</p>
            <h2 className="gx-serif text-4xl md:text-5xl mt-5">The gold market, in real time.</h2>
            <div className="mt-6 flex items-end gap-5 flex-wrap">
              <span className="gx-serif text-5xl md:text-6xl tabular-nums" data-testid="gtax-chart-price">{spot?.price ? `$${f2(spot.price)}` : "—"}</span>
              {chg != null && <span className={`pb-2 tabular-nums ${up ? "text-emerald-600" : "text-red-600"}`} data-testid="gtax-chart-change">
                {up ? "+" : "−"}{f2(Math.abs(chg))} ({up ? "+" : "−"}{f2(Math.abs((chg / first) * 100))}%) · {RANGES.find((r) => r[0] === range)[1]}</span>}
            </div>
          </div>
          <div className="flex gap-1 border border-[color:var(--line)] p-1" data-testid="gtax-chart-ranges">
            {RANGES.map(([k, l]) => (
              <button key={k} onClick={() => setRange(k)} data-testid={`gtax-chart-range-${k}`}
                className={`px-5 py-2 text-xs tracking-widest font-semibold transition-colors ${range === k ? "bg-[#8F6B22] text-white" : "text-[color:var(--mute)] hover:text-[color:var(--ink)]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mt-10 h-[340px] md:h-[420px] gx-card p-4 md:p-6" data-testid="gtax-chart-canvas">
          {series.length > 1 && (
            <ResponsiveContainer>
              <AreaChart data={series} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
                <defs><linearGradient id="gxc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C9A24A" stopOpacity={0.28} /><stop offset="100%" stopColor="#C9A24A" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="rgba(201,162,74,.10)" vertical={false} />
                <XAxis dataKey="t" tickFormatter={tick(range)} stroke="#8C8372" fontSize={11} tickLine={false} axisLine={false} minTickGap={48} />
                <YAxis domain={["dataMin - 5", "dataMax + 5"]} orientation="right" stroke="#8C8372" fontSize={11} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => v.toFixed(0)} />
                <Tooltip contentStyle={{ background: "#12100C", border: "1px solid rgba(201,162,74,.3)", borderRadius: 2 }} labelStyle={{ color: "#8C8372" }}
                  labelFormatter={(t) => new Date(t * 1000).toLocaleString()} formatter={(v) => [`$${f2(v)}`, "XAU/USD"]} />
                <Area type="monotone" dataKey="v" stroke="#C9A24A" strokeWidth={1.8} fill="url(#gxc)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat k="Period high" v={vals.length ? `$${f2(Math.max(...vals))}` : "—"} id="gtax-chart-high" />
          <Stat k="Period low" v={vals.length ? `$${f2(Math.min(...vals))}` : "—"} id="gtax-chart-low" />
          <Stat k="Session" v={spot?.market_open === false ? "Closed" : "Open"} id="gtax-chart-session" />
          <Stat k="Updated" v={spot?.price_at ? new Date(spot.price_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} id="gtax-chart-updated" />
        </div>
        <p className="mt-6 text-xs text-[color:var(--mute)]">Spot price from public market data, refreshed every 30 seconds; chart history from COMEX gold futures. Indicative only, not a quote or an offer.</p>
      </div>
    </section>
  );
};
